import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import axios from 'axios';
import CreatableSelect from 'react-select/creatable';
import './QuickRsvpPage.css';

const MEMORY_KEY = 'connaught.quick-rsvp.identity.v1';
function rememberedIdentity() {
    try {
        const current = JSON.parse(localStorage.getItem(MEMORY_KEY));
        if (current) return current;
        const legacy = JSON.parse(localStorage.getItem('connaught361:rsvp-responder-memory:v1') || '{}');
        const profile = legacy.profiles?.[legacy.lastKey];
        return profile ? {name: profile.name || '', lodge: /^other/i.test(profile.lodge || '') ? profile.otherLodge || '' : profile.lodge || ''} : {};
    } catch { return {}; }
}

function localPeople(search) {
    try {
        const current = JSON.parse(localStorage.getItem(MEMORY_KEY));
        if (current?.disabled) return [];
        const legacy = JSON.parse(localStorage.getItem('connaught361:rsvp-responder-memory:v1') || '{}');
        const profiles = [current, ...Object.values(legacy.profiles || {})].filter(Boolean);
        const seen = new Set();
        return profiles.map(profile => ({name:profile.name || '', lodge:/^other/i.test(profile.lodge || '') ? profile.otherLodge || '' : profile.lodge || ''}))
            .filter(person => { const key=person.name+'|'+person.lodge; if (!person.name.toLowerCase().includes(search.toLowerCase()) || seen.has(key)) return false; seen.add(key); return true; }).slice(0,8);
    } catch { return []; }
}

export default function QuickRsvpPage({user}) {
    const location = useLocation();
    const [identity] = useState(rememberedIdentity);
    const [name, setName] = useState(identity.name || '');
    const [lodge, setLodge] = useState(identity.lodge || '');
    const [remember, setRemember] = useState(Boolean(identity.name));
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [ownProfiles, setOwnProfiles] = useState([]);
    const [nameFocused, setNameFocused] = useState(false);
    const nameTouched = useRef(false);
    const lodgeTouched = useRef(false);
    useEffect(() => {
        if (!nameTouched.current && !identity.name && user?.first_name && user?.last_name) {
            setName(`${user.first_name} ${user.last_name}`.trim());
        }
    }, [user, identity]);
    const submitting = useRef(false);
    const link = useMemo(() => ({
        slug: new URLSearchParams(location.search).get('survey') || '',
        token: new URLSearchParams(location.hash.slice(1)).get('token') || '',
    }), [location.search, location.hash]);
    const currentLink = useRef(link);
    currentLink.current = link;
    useEffect(() => {
        let active = true;
        setLoading(true); setError(''); setSurvey(null); setSaved(null);
        if (!link.slug) { setError('This RSVP link is incomplete. Please ask the Secretary for the meeting link.'); setLoading(false); return; }
        axios.post(`/api/surveys/${encodeURIComponent(link.slug)}/verify-token`, {token: link.token})
            .then(({data}) => {
                if (!active) return;
                if (data.definition?.kind !== 'meeting_attendance') throw new Error('This link is not for an RSVP forecast. Please use the original form.');
                setSurvey(data);
            }).catch(err => { if (active) setError(err.response?.data?.error || err.message || 'This meeting is closed or the link is invalid.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [link]);
    const questions = survey?.definition?.sections?.flatMap(section => section.questions || []) || [];
    const lodgeOptions = questions.find(q => q.id === 'lodge_affiliation')?.options || [];
    useEffect(() => {
        if (!survey || !user?.id || String(user.role || '').toLowerCase() === 'user') { setOwnProfiles([]); return; }
        let active = true;
        axios.post(`/api/surveys/${encodeURIComponent(link.slug)}/quick-rsvp-suggestions`, {token:link.token})
            .then(({data}) => {
                if (!active) return;
                setOwnProfiles(data);
                if (!nameTouched.current && !identity.name && data.length) {
                    setName(data[0].name);
                    if (!lodgeTouched.current && data.length === 1) setLodge(data[0].lodge || '');
                }
            }).catch(() => { if (active) setOwnProfiles([]); });
        return () => { active = false; };
    }, [survey, user, link, identity]);
    useEffect(() => {
        if (!survey || name.trim().length < 3 || saved) { setSuggestions([]); return; }
        const people = [...localPeople(name.trim()), ...ownProfiles.filter(person => person.name.toLowerCase().includes(name.trim().toLowerCase()))];
        const unique = people.filter((person,index) => people.findIndex(other => other.name === person.name && other.lodge === person.lodge) === index).slice(0,8);
        setSuggestions(unique);
        const exact = unique.filter(person => person.name.trim().toLowerCase() === name.trim().toLowerCase());
        const lodges = [...new Set(exact.map(person => person.lodge).filter(Boolean))];
        if (!lodgeTouched.current && lodges.length === 1) setLodge(lodges[0]);
    }, [name, survey, saved, ownProfiles]);
    function choosePerson(person) {
        setName(person.name); nameTouched.current = true;
        setLodge(person.lodge || ''); lodgeTouched.current = false;
        setNameFocused(false);
    }
    async function submit(event) {
        event.preventDefault();
        if (submitting.current) return;
        const attendance = event.nativeEvent.submitter?.value;
        if (!['Yes', 'No'].includes(attendance)) return;
        if (name.trim().split(/\s+/).length < 2) { setError('Please enter your name and surname.'); return; }
        const exact = lodgeOptions.find(option => option.toLowerCase() === lodge.trim().toLowerCase() && !option.toLowerCase().startsWith('other'));
        const answers = {
            name: name.trim(), attendance_status: attendance,
            lodge_affiliation: lodgeOptions.length ? (exact || 'Other') : lodge.trim(),
            ...(lodgeOptions.length && !exact ? {lodge_affiliation__other: lodge.trim()} : {}),
        };
        submitting.current = true; setBusy(true); setError('');
        try {
            const {data} = await axios.post(`/api/surveys/${encodeURIComponent(link.slug)}/responses`, {token: link.token, answers, quickRsvp: true});
            if (currentLink.current !== link) return;
            if (data.success === false) throw new Error('Your response could not be saved. Please try again.');
            try {
                if (remember) localStorage.setItem(MEMORY_KEY, JSON.stringify({name: name.trim(), lodge: lodge.trim()}));
                else localStorage.setItem(MEMORY_KEY, JSON.stringify({disabled: true}));
            } catch { /* Saving the RSVP succeeds even when browser storage is unavailable. */ }
            setSaved({name: name.trim(), lodge: lodge.trim(), attendance});
        } catch (err) { setError(err.response?.data?.error || err.message || 'Could not save your RSVP. Please try again.'); }
        finally { submitting.current = false; setBusy(false); }
    }
    return <main className="quick-rsvp">
        <p className="quick-rsvp-eyebrow">Connaught Lodge · Meeting RSVP</p>
        <h1>{survey?.definition?.meeting?.lodge || survey?.title || 'Meeting RSVP'}</h1>
        {survey?.definition?.meeting?.type && <p className="quick-rsvp-type">{survey.definition.meeting.type}</p>}
        {survey?.definition?.meeting?.date && <p className="quick-rsvp-date">{new Intl.DateTimeFormat('en-ZA', {dateStyle:'full'}).format(new Date(`${survey.definition.meeting.date}T12:00:00`))}</p>}
        {loading && <p role="status">Opening your meeting…</p>}
        {error && <p className="quick-rsvp-error" role="alert">{error}</p>}
        {saved ? <section role="status" className="quick-rsvp-success">
            <h2>Thank you, {saved.name}.</h2>
            <p>{saved.attendance === 'Yes' ? 'Your attendance is confirmed.' : 'Your apologies have been recorded.'}</p>
            <p>{saved.lodge}</p>
            <button type="button" onClick={() => setSaved(null)}>Change my response</button>
            <button type="button" onClick={() => { setSaved(null); setName(''); setLodge(''); setRemember(false); nameTouched.current = true; lodgeTouched.current = false; }}>Respond for someone else</button>
        </section> : survey && <form onSubmit={submit}>
            <label htmlFor="quick-rsvp-name">Your name and surname</label>
            <input id="quick-rsvp-name" placeholder="Start typing your name" autoComplete="name" maxLength={150} required value={name} onFocus={() => setNameFocused(true)} onBlur={e => { if (!e.relatedTarget?.closest('.quick-rsvp-people')) setNameFocused(false); }} onChange={e => { nameTouched.current = true; lodgeTouched.current = false; setName(e.target.value); setLodge(''); }} />
            {nameFocused && suggestions.length > 0 && <div className="quick-rsvp-people" aria-label="Matching people" onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget) && e.relatedTarget?.id !== 'quick-rsvp-name') setNameFocused(false); }}>
                <small>Saved on this device or your account — tap to use</small>
                {suggestions.map((person, index) => <button type="button" key={`${person.name}-${person.lodge}-${index}`} onMouseDown={e => e.preventDefault()} onClick={() => choosePerson(person)}><strong>{person.name}</strong><span>{person.lodge || 'Choose your lodge below'}</span></button>)}
            </div>}
            <label htmlFor="quick-rsvp-lodge">Your lodge</label>
            <CreatableSelect inputId="quick-rsvp-lodge" className="quick-rsvp-select" classNamePrefix="quick-lodge" name="lodge" required isClearable isSearchable
                options={lodgeOptions.filter(option => !option.toLowerCase().startsWith('other')).map(option => ({value:option,label:option}))}
                value={lodge ? {value:lodge,label:lodge} : null}
                onChange={option => { lodgeTouched.current = true; setLodge(option?.value || ''); }}
                placeholder="Choose or search your lodge"
                formatCreateLabel={value => `Use lodge: ${value}`}
                noOptionsMessage={() => 'Type your lodge name and number'}
                maxMenuHeight={220} menuPlacement="auto" />
            <p className="quick-rsvp-help">Not listed? Type its name and number, then choose “Use lodge”.</p>
            <label className="quick-rsvp-remember"><input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />Remember my name and lodge on this device</label>
            <p className="quick-rsvp-help">No login needed. Only remember details on your own device.</p>
            <h2>Will you attend?</h2>
            <div className="quick-rsvp-actions">
                <button name="attendance" value="Yes" disabled={busy}>{busy ? 'Saving…' : 'Yes, attending'}</button>
                <button name="attendance" value="No" disabled={busy}>No, apologies</button>
            </div>
            <p className="quick-rsvp-help">Use the same name and lodge when updating a reply. Please check with the Secretary if two people share these details.</p>
        </form>}
        <p className="quick-rsvp-help"><a href={link.slug ? `/surveys?survey=${encodeURIComponent(link.slug)}` : '/surveys'}>Open the original survey page</a></p>
    </main>;
}
